import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { X, PanelLeft, SquarePen, Trash, Check, Plus } from "lucide-react";
import { getSidebarConfig, saveSidebarConfig } from "../api/sidebarConfig.api";

const EMPTY_DRAFT = { name: "", url: "" };
const URL_PATTERN = /^https?:\/\/.+/i;

const genKey = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `key-${Date.now()}-${Math.random()}`;

const SidebarConfigModal = ({ isOpen, onClose, customerId, customerName }) => {
  const [buttons, setButtons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editingDraft, setEditingDraft] = useState(EMPTY_DRAFT);
  const [editingError, setEditingError] = useState("");
  const [newButtonDraft, setNewButtonDraft] = useState(EMPTY_DRAFT);
  const [newButtonError, setNewButtonError] = useState("");

  useEffect(() => {
    if (!isOpen || !customerId) return;

    let ignore = false;
    setLoading(true);
    getSidebarConfig(customerId)
      .then((data) => {
        if (!ignore) {
          const loaded = Array.isArray(data?.buttons) ? data.buttons : [];
          setButtons(loaded.map((btn) => ({ ...btn, _key: genKey() })));
        }
      })
      .catch(() => { if (!ignore) setButtons([]); })
      .finally(() => { if (!ignore) setLoading(false); });

    return () => { ignore = true; };
  }, [isOpen, customerId]);

  if (!isOpen) return null;

  const startEdit = (key) => {
    const target = buttons.find((btn) => btn._key === key);
    if (!target) return;
    setEditingKey(key);
    setEditingDraft({ name: target.name, url: target.url });
    setEditingError("");
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditingDraft(EMPTY_DRAFT);
    setEditingError("");
  };

  const confirmEdit = () => {
    const name = editingDraft.name.trim();
    const url = editingDraft.url.trim();
    if (!name || !url) return;
    if (!URL_PATTERN.test(url)) {
      setEditingError("La URL debe comenzar con http:// o https://");
      return;
    }
    setButtons((prev) => prev.map((btn) => (btn._key === editingKey ? { ...btn, name, url } : btn)));
    cancelEdit();
  };

  const handleDelete = (key) => {
    setButtons((prev) => prev.filter((btn) => btn._key !== key));
    if (editingKey === key) cancelEdit();
  };

  const handleAddButton = () => {
    const name = newButtonDraft.name.trim();
    const url = newButtonDraft.url.trim();
    if (!name || !url) return;
    if (!URL_PATTERN.test(url)) {
      setNewButtonError("La URL debe comenzar con http:// o https://");
      return;
    }
    setNewButtonError("");
    setButtons((prev) => [...prev, { name, url, _key: genKey() }]);
    setNewButtonDraft(EMPTY_DRAFT);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = buttons.map(({ _key, ...rest }) => rest);
      await saveSidebarConfig(customerId, payload);
      toast.success("Barra lateral actualizada.");
      onClose();
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data?.message || error.response.data?.error || "Error al guardar la barra lateral.");
      } else {
        console.error("Error al guardar la barra lateral:", error);
        toast.error("Error al guardar la barra lateral.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col">

        <div className="p-5 border-b dark:border-gray-700 flex justify-between items-center bg-blue-50 dark:bg-gray-900/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
              <PanelLeft className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-bold text-gray-800 dark:text-white leading-tight">
                Barra lateral
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">{customerName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="text-gray-500 text-sm font-medium">Cargando botones...</p>
            </div>
          ) : (
            <>
              {buttons.length === 0 ? (
                <p className="text-center text-gray-500 py-4 text-sm">Todavía no hay botones configurados.</p>
              ) : (
                <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-sm rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-100 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Nombre</th>
                        <th className="px-4 py-2 text-left text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">URL</th>
                        <th className="px-4 py-2 text-center text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {buttons.map((button) => (
                        <tr key={button._key} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                          {editingKey === button._key ? (
                            <>
                              <td className="px-4 py-2">
                                <input
                                  type="text"
                                  value={editingDraft.name}
                                  onChange={(e) => setEditingDraft((prev) => ({ ...prev, name: e.target.value }))}
                                  className="w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <input
                                  type="text"
                                  value={editingDraft.url}
                                  onChange={(e) => {
                                    setEditingDraft((prev) => ({ ...prev, url: e.target.value }));
                                    if (editingError) setEditingError("");
                                  }}
                                  className="w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-1"
                                />
                                {editingError && (
                                  <p className="text-xs text-red-500 mt-1">{editingError}</p>
                                )}
                              </td>
                              <td className="px-4 py-2 text-center">
                                <div className="flex justify-center space-x-2">
                                  <button onClick={confirmEdit} className="p-2 rounded-full bg-green-100 text-green-600 hover:bg-green-200" title="Confirmar">
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button onClick={cancelEdit} className="p-2 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200" title="Cancelar">
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">{button.name}</td>
                              <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 break-all">{button.url}</td>
                              <td className="px-4 py-2 text-center">
                                <div className="flex justify-center space-x-2">
                                  <button onClick={() => startEdit(button._key)} className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200" title="Editar">
                                    <SquarePen className="w-4 h-4" />
                                  </button>
                                  <button onClick={() => handleDelete(button._key)} className="p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200" title="Eliminar">
                                    <Trash className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
                <div className="flex-1">
                  <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">Nombre</label>
                  <input
                    type="text"
                    value={newButtonDraft.name}
                    onChange={(e) => setNewButtonDraft((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Instagram"
                    className="w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-2"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase mb-1">URL</label>
                  <input
                    type="text"
                    value={newButtonDraft.url}
                    onChange={(e) => {
                      setNewButtonDraft((prev) => ({ ...prev, url: e.target.value }));
                      if (newButtonError) setNewButtonError("");
                    }}
                    placeholder="https://instagram.com/..."
                    className="w-full text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-2 py-2"
                  />
                  {newButtonError && (
                    <p className="text-xs text-red-500 mt-1">{newButtonError}</p>
                  )}
                </div>
                <button
                  onClick={handleAddButton}
                  disabled={!newButtonDraft.name.trim() || !newButtonDraft.url.trim()}
                  className="flex items-center justify-center gap-1 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                  Agregar
                </button>
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-all text-sm font-bold shadow-sm"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all text-sm font-bold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SidebarConfigModal;
